import {
  ApolloLink,
  Observable,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client';
import { getBrandSiteHost } from '../../utils/siteHost';
import { authActions, authStore } from '../../../store/authStore';
import { REFRESH_TOKENS } from '../authService/queries';
import { jwtDecode } from 'jwt-decode';
import { onError } from '@apollo/client/link/error';
import { getCurrentRuntimeTarget, getGraphqlBaseUrl } from '../../../config/runtime-target';

const REFRESH_THRESHOLD = 30;
let isRefreshing = false;

const isDealershipSite =
  window.location.hostname === 'autopiloteecars.com' ||
  window.location.hostname === 'www.autopiloteecars.com' ||
  window.location.hostname === 'jidosojuholding.com' ||
  window.location.hostname === 'www.jidosojuholding.com' ||
  window.location.hostname === 'instasharings.com' ||
  window.location.hostname === 'www.instasharings.com';

const runtimeTarget = getCurrentRuntimeTarget();
const apiUrl = getGraphqlBaseUrl();

const getDevClockHeaders = (): Record<string, string> => {
  if (runtimeTarget === 'production' || runtimeTarget === 'staging') {
    return {};
  }

  try {
    const enabled = localStorage.getItem('app_time_enabled') === 'true';
    const nowMs = localStorage.getItem('app_time_mocked_utc_ms');
    if (!enabled || !nowMs || !/^\d+$/.test(nowMs)) {
      return {};
    }

    const timeZoneId = localStorage.getItem('app_time_timezone');
    return {
      'x-dev-app-now-ms': nowMs,
      ...(timeZoneId ? { 'x-dev-app-timezone': timeZoneId } : {}),
    };
  } catch {
    return {};
  }
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isAuthError = graphQLErrors?.some(
    e =>
      e.message?.toLowerCase?.().includes('session expired') ||
      e.message?.toLowerCase?.().includes('unauthorized') ||
      e.message?.toLowerCase?.().includes('invalid token') ||
      e.message?.toLowerCase?.().includes('access token has expired') ||
      e.extensions?.code === 'UNAUTHENTICATED' ||
      e.extensions?.code === 'NO_TOKEN_PROVIDED',
  );

  if (isAuthError) {
    const { refreshToken } = authStore.tokens;
    if (refreshToken && !isRefreshing) {
      isRefreshing = true;
      return new Observable(observer => {
        client
          .mutate({
            mutation: REFRESH_TOKENS,
            variables: { refreshToken },
            context: {
              headers: {
                'id-token': authStore.tokens.idToken || '',
                'refresh-token': refreshToken,
                ...getDevClockHeaders(),
              },
            },
          })
          .then(({ data }) => {
            if (data?.refreshTokens) {
              const newTokens = {
                accessToken: data.refreshTokens.accessToken,
                idToken: data.refreshTokens.idToken,
                refreshToken: data.refreshTokens.refreshToken,
              };
              authStore.tokens = newTokens;
              authStore.isAuthenticated = true; // ✅ Explicitly maintain authentication state
              localStorage.setItem('tokens', JSON.stringify(newTokens));
              // ✅ Also save user to localStorage if it exists
              if (authStore.user) {
                localStorage.setItem('user', JSON.stringify(authStore.user));
              }

              operation.setContext({
                headers: {
                  ...operation.getContext().headers,
                  authorization: `Bearer ${newTokens.accessToken}`,
                  'id-token': newTokens.idToken || '',
                  'refresh-token': newTokens.refreshToken || '',
                  'content-type': 'application/json',
                  ...getDevClockHeaders(),
                },
              });
              forward(operation).subscribe(observer);
            } else {
              authActions.logout();
              observer.error(new Error('Session expired'));
            }
          })
          .catch(() => {
            authActions.logout();
            observer.error(new Error('Session expired'));
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }
  }

  if (networkError) {
  }
});

export const authLink = new ApolloLink((operation, forward) => {
  const { accessToken, refreshToken, idToken } = authStore.tokens;
  const userEmail = authStore.user?.email;

  const shouldRefreshToken = () => {
    if (!accessToken) return false;
    try {
      const decoded = jwtDecode<{ exp: number }>(accessToken);
      const willExpireIn = decoded.exp * 1000 - Date.now();
      return willExpireIn < REFRESH_THRESHOLD * 1000;
    } catch (error) {
      return false;
    }
  };

  if (shouldRefreshToken() && refreshToken && !isRefreshing) {
    isRefreshing = true;
    return new Observable(observer => {
      client
        .mutate({
          mutation: REFRESH_TOKENS,
          variables: { refreshToken },
          context: {
            headers: {
              'id-token': idToken || '',
              'refresh-token': refreshToken,
              ...getDevClockHeaders(),
            },
          },
        })
        .then(({ data }) => {
          if (data?.refreshTokens) {
            const newTokens = {
              accessToken: data.refreshTokens.accessToken,
              idToken: data.refreshTokens.idToken,
              refreshToken: data.refreshTokens.refreshToken,
            };
            authStore.tokens = newTokens;
            authStore.isAuthenticated = true; // ✅ Explicitly maintain authentication state
            localStorage.setItem('tokens', JSON.stringify(newTokens)); // ✅ Save to localStorage
            // ✅ Also save user to localStorage if it exists
            if (authStore.user) {
              localStorage.setItem('user', JSON.stringify(authStore.user));
            }

            operation.setContext({
              headers: {
                ...operation.getContext().headers,
                authorization: `Bearer ${newTokens.accessToken}`,
                'id-token': newTokens.idToken || '',
                'refresh-token': newTokens.refreshToken || '',
                'content-type': 'application/json',
                ...getDevClockHeaders(),
              },
            });
            forward(operation).subscribe(observer);
          } else {
            authActions.clearTokens();
            forward(operation).subscribe(observer);
          }
          isRefreshing = false;
        })
        .catch(e => {
          authActions.clearTokens();
          isRefreshing = false;
          forward(operation).subscribe(observer);
        });
    });
  }

  operation.setContext({
    headers: {
      ...operation.getContext().headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
      'id-token': idToken || '',
      'refresh-token': refreshToken || '',
      'content-type': 'application/json',
      // Force CORS preflight to satisfy Apollo CSRF prevention across all operations
      'apollo-require-preflight': 'true',
      // Provide operation name header to satisfy Apollo CSRF prevention
      'x-apollo-operation-name': operation.operationName || 'UnknownOperation',
      ...getDevClockHeaders(),
    },
  });

  return forward(operation);
});

/**
 * The brand this site serves (D37), on every request that reaches the server.
 * This is the last link before the transport, so a request resent after a
 * token refresh (authLink and errorLink rebuild the headers there, and a
 * failed refresh resends with none) still names the site. Without it the
 * backend treated such a request as autopilotee.com: a guest on lenvoy.co was
 * told to "sign in at lenvoy.co", and a checkout in that window would have
 * been recorded and charged on the wrong brand (spec 012).
 */
export const siteHostLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
    headers: { ...headers, 'X-Site-Host': getBrandSiteHost() },
  }));
  return forward(operation);
});

const httpLink = createHttpLink({
  uri: `${apiUrl}graphql`,
  credentials: 'include',
  headers: {
    'X-Site-Origin': isDealershipSite ? 'dealership' : 'main',
    // Force CORS preflight to satisfy Apollo CSRF prevention
    'apollo-require-preflight': 'true',
  },
});

export const client = new ApolloClient({
  link: from([errorLink, authLink, siteHostLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      // Spec 019: a worker has no `id`; its car id names it, so a save updates every view of it.
      ProviderWorker: {
        keyFields: ['carId'],
        fields: { worker: { merge: true }, settings: { merge: true } },
      },
      // A job's timeline is named by its booking: a check-in updates the open timeline.
      JobTimeline: { keyFields: ['bookingId'] },
      // A site's public rules are read by two queries (spec 023); named by the site, their
      // fields merge instead of one answer replacing the other.
      PublicBrandRules: { keyFields: ['brandSite'] },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
  devtools: {
    enabled: true,
  },
});
